import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import './DemoPage.css';
import './SplashAppend.css';
import logoImage from './assets/Nigeria.png';
import splashLogo from './assets/Nigeria1.png';
import sampleXray from './assets/sample_cxr.png';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5001';

const STEPS = [
  { n: 1, label: 'Upload X-Ray' },
  { n: 2, label: 'Run Analysis' },
  { n: 3, label: 'Review Results' },
];

/* ══ PORTAL TOOLTIP ══════════════════════════════════════════════ */
function PortalTip({ anchorRef, text, arrow = '←', offset = 12, delay = 1200, placement = 'right' }) {
  const [pos, setPos] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  useEffect(() => {
    const update = () => {
      if (!anchorRef.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return; // not yet in DOM
      if (placement === 'right') {
        setPos({ top: r.top + r.height / 2, left: r.right + offset, transform: 'translateY(-50%)' });
      } else if (placement === 'top') {
        setPos({ top: r.top - offset, left: r.left + r.width * 0.5, transform: 'translate(-50%, -100%)' });
      } else if (placement === 'top-left') {
        setPos({ top: r.top - offset, left: r.left + r.width * 0.3, transform: 'translate(-50%, -100%)' });
      }
    };
    // small delay so DOM has painted before we measure
    const t = setTimeout(update, 100);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      clearTimeout(t);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef, offset, placement]);

  if (!pos || !visible) return null;
  return ReactDOM.createPortal(
    <div className={`dw-portal-tip dw-portal-tip-${placement}`}
      style={{ top: pos.top, left: pos.left, transform: pos.transform }}>
      <span className="dw-portal-arrow">{arrow}</span>
      <span>{text}</span>
    </div>,
    document.body
  );
}

/* ══ SPLASH SCREEN ════════════════════════════════════════════════ */
function SplashScreen({ onDone }) {
  const [brandIn, setBrandIn] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setBrandIn(true), 100);
    const t2 = setTimeout(() => setExiting(true), 2200);
    const t3 = setTimeout(() => onDone(),          2800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDone]);

  return (
    <div className={`sp-root ${exiting ? 'sp-exit' : ''}`} aria-hidden="true">
      <div className={`sp-brand ${brandIn ? 'sp-brand-in' : ''}`}>
        <img src={splashLogo} alt="INL Diagnostics AI" className="sp-logo" />
        <div className="sp-brand-text">
          <p className="sp-company">INL Diagnostics AI</p>
          <p className="sp-tagline">Clinical Decision Support Platform</p>
        </div>
      </div>
      <div className={`sp-footer-line ${brandIn ? 'sp-footer-in' : ''}`} />
    </div>
  );
}

/* ── Icons ────────────────────────────────────────────────────────── */
function UploadIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

/* ══ MAIN PAGE ════════════════════════════════════════════════════ */
export default function DemoPage() {
  const fileInputRef   = useRef(null);
  const sliderRef      = useRef(null);
  const dragging       = useRef(false);
  const sampleBtnRef   = useRef(null);
  const runBtnRef      = useRef(null);
  const reportBtnRef   = useRef(null);
  const sliderKnobRef  = useRef(null);

  const [splashDone,      setSplashDone]      = useState(false);
  const [tourStep,        setTourStep]        = useState(0); // 0=upload 1=run 2=slider 3=report 4=done
  const [selectedFile,    setSelectedFile]    = useState(null);
  const [previewUrl,      setPreviewUrl]      = useState(null);
  const [patientRef,      setPatientRef]      = useState('');
  const [prediction,      setPrediction]      = useState(null);
  const [confidence,      setConfidence]      = useState(null);
  const [heatmapUrl,      setHeatmapUrl]      = useState(null);
  const [explanation,     setExplanation]     = useState(null);
  const [region,          setRegion]          = useState(null);
  const [timestamp,       setTimestamp]       = useState(null);
  const [imageFilename,   setImageFilename]   = useState(null);
  const [gradcamFilename, setGradcamFilename] = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [reportLoading,   setReportLoading]   = useState(false);
  const [error,           setError]           = useState('');
  const [sliderPos,       setSliderPos]       = useState(50);
  const [dragOver,        setDragOver]        = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const currentStep = loading ? 2 : prediction ? 4 : selectedFile ? 2 : 1;

  const updateSlider = (clientX) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    setSliderPos(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
  };
  const onMouseDown  = (e) => { dragging.current = true;  updateSlider(e.clientX); };
  const onMouseMove  = (e) => { if (dragging.current) updateSlider(e.clientX); };
  const onMouseUp    = ()  => { dragging.current = false; };
  const onTouchStart = (e) => { dragging.current = true;  updateSlider(e.touches[0].clientX); };
  const onTouchMove  = (e) => { updateSlider(e.touches[0].clientX); };
  const onTouchEnd   = ()  => { dragging.current = false; };

  const applyFile = (file) => {
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setPrediction(null); setConfidence(null); setHeatmapUrl(null);
    setExplanation(null); setRegion(null); setTimestamp(null);
    setImageFilename(null); setGradcamFilename(null);
    setError('');
    setTourStep(s => s === 0 ? 1 : s);
  };

  const handleFileChange = (e) => applyFile(e.target.files[0]);
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); applyFile(e.dataTransfer.files[0]); };

  const loadSample = async () => {
    try {
      const res  = await fetch(sampleXray);
      const blob = await res.blob();
      const file = new File([blob], 'sample_chest_xray.png', { type: 'image/png' });
      applyFile(file);
    } catch (err) {
      setError('Could not load sample image. Please upload your own.');
    }
  };

  const clearAll = () => {
    setSelectedFile(null); setPreviewUrl(null);
    setPrediction(null); setConfidence(null);
    setHeatmapUrl(null); setExplanation(null);
    setRegion(null); setTimestamp(null);
    setError('');
    setTourStep(0);
  };

  const handleAnalyze = async () => {
    if (!selectedFile) { setError('Please upload a chest X-ray image first.'); return; }
    setTourStep(s => s === 1 ? 0 : s); // dismiss step 1 tip if still showing
    const formData = new FormData();
    formData.append('image', selectedFile);
    setLoading(true); setError('');
    setPrediction(null); setConfidence(null); setHeatmapUrl(null);
    setExplanation(null); setRegion(null);
    try {
      const ctrl = new AbortController();
      const tid  = setTimeout(() => ctrl.abort(), 120000);
      let res;
      try {
        res = await fetch(`${API}/predict`, { method: 'POST', body: formData, signal: ctrl.signal });
      } finally { clearTimeout(tid); }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Prediction failed.');
      setPrediction(data.prediction);
      setConfidence(data.confidence);
      setHeatmapUrl(data.heatmap_url);
      setExplanation(data.explanation || null);
      setRegion(data.region || null);
      setTimestamp(data.timestamp || null);
      setImageFilename(data.image_filename || null);
      setGradcamFilename(data.gradcam_filename || null);
      setTourStep(2);
    } catch (err) {
      setError(err.name === 'AbortError'
        ? 'Request timed out. The server may be starting up — please try again.'
        : err.message || 'Unable to connect to the prediction server.');
    } finally { setLoading(false); }
  };

  const handleDownloadReport = async () => {
    if (!prediction) return;
    setReportLoading(true);
    try {
      const res = await fetch(`${API}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_ref: patientRef.trim() || null,
          prediction, confidence, explanation, region, timestamp,
          image_filename: imageFilename,
          gradcam_filename: gradcamFilename,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Report generation failed.');
      window.open(data.report_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message || 'Could not generate PDF report.');
    } finally { setReportLoading(false); }
  };

  const isTB      = prediction === 'TB Detected';
  const hasResult = prediction !== null;

  return (
    <>
      {/* Splash shows on every load/refresh until dismissed */}
      {!splashDone && <SplashScreen onDone={() => setSplashDone(true)} />}

      {/* Main workstation fades in after splash */}
      <div className={`dw-root ${splashDone ? 'dw-root-visible' : 'dw-root-hidden'}`}>

        <header className="dw-topbar">
          <div className="dw-topbar-l">
            <img src={logoImage} alt="TBVision logo" className="dw-logo" />
            <div className="dw-brand-group">
              <span className="dw-brand-name">TBVision AI</span>
              <span className="dw-brand-tag">Clinical Workstation</span>
            </div>
          </div>

          <nav className="dw-steps" aria-label="Workflow steps">
            {STEPS.map((s, i) => (
              <div key={s.n} className="dw-step-item">
                <div className={`dw-step-node ${currentStep > s.n ? 'done' : currentStep === s.n ? 'active' : ''}`}>
                  {currentStep > s.n
                    ? <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    : s.n}
                </div>
                <span className={`dw-step-label ${currentStep === s.n ? 'active' : ''}`}>{s.label}</span>
                {i < STEPS.length - 1 && <div className={`dw-step-line ${currentStep > s.n ? 'done' : ''}`} />}
              </div>
            ))}
          </nav>

          <div className="dw-topbar-r">
            <Link to="/" className="dw-back-btn">← Overview</Link>
          </div>
        </header>

        <main className="dw-workspace">
          <aside className="dw-left">
            <div className="dw-panel-header">
              <h2 className="dw-panel-title">Patient & Image</h2>
            </div>

            <div className="dw-field">
              <label className="dw-field-label" htmlFor="pat-ref">
                Patient Reference <span className="dw-optional">optional</span>
              </label>
              <input id="pat-ref" type="text" className="dw-input"
                placeholder="e.g. PAT-2026-0041" value={patientRef}
                onChange={(e) => setPatientRef(e.target.value)} />
            </div>

            <div className="dw-field">
              <label className="dw-field-label">Chest X-Ray Image</label>

              {/* Sample image banner */}
              {!previewUrl && (
                <div className="dw-sample-banner">
                  <div className="dw-sample-banner-left">
                    <span className="dw-sample-tag">No X-ray?</span>
                    <span className="dw-sample-text">Try a real chest X-ray from our dataset</span>
                  </div>
                  <button ref={sampleBtnRef} className="dw-sample-banner-btn" onClick={loadSample}>
                    Load sample →
                  </button>
                </div>
              )}

              <div className={`dw-dropzone ${previewUrl ? 'has-image' : ''} ${dragOver ? 'drag-over' : ''}`}
                onClick={() => !previewUrl && fileInputRef.current.click()}
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                role="button" aria-label="Upload chest X-ray image" tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current.click()}>
                {previewUrl ? (
                  <div className="dw-preview-wrap">
                    <img src={previewUrl} alt="Selected X-ray" className="dw-preview-img" />
                    <div className="dw-preview-overlay">
                      <button className="dw-change-btn"
                        onClick={(e) => { e.stopPropagation(); fileInputRef.current.click(); }}>
                        Change image
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="dw-drop-placeholder">
                    <div className="dw-drop-icon"><UploadIcon /></div>
                    <p className="dw-drop-title">{dragOver ? 'Release to upload' : 'Drop X-ray here'}</p>
                    <p className="dw-drop-sub">or <span className="dw-drop-link">click to browse</span></p>
                    <p className="dw-drop-formats">JPG · PNG · DICOM</p>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*,.dcm"
                  onChange={handleFileChange} style={{ display: 'none' }} />
              </div>
              {selectedFile && (
                <div className="dw-file-strip">
                  <span className="dw-file-name">📄 {selectedFile.name}</span>
                  <button className="dw-clear-btn" onClick={clearAll}>Clear</button>
                </div>
              )}
            </div>

            {error && <div className="dw-error" role="alert"><span>⚠</span> {error}</div>}

            <div className="dw-tour-wrap dw-tour-wrap-block">
              <button ref={runBtnRef} className={`dw-run-btn ${loading ? 'loading' : ''} ${tourStep === 1 ? 'dw-run-btn-pulse' : ''}`}
              onClick={handleAnalyze} disabled={loading || !selectedFile} aria-busy={loading}>
              {loading ? (
                <><span className="dw-spinner" /> Analyzing…</>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                  </svg>
                  Run Analysis
                </>
              )}
            </button>
            </div>

            <div className="dw-disclaimer">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0, marginTop: 1 }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <p>This tool is an AI-assisted decision-support system. It does <strong>not</strong> constitute
                a clinical diagnosis. All results must be reviewed by a qualified clinician before any
                clinical action is taken.</p>
            </div>
          </aside>

          <section className="dw-right" aria-live="polite" aria-atomic="false">
            {!hasResult && !loading && (
              <div className="dw-empty">
                <div className="dw-empty-visual"><div className="dw-empty-icon"><ImageIcon /></div></div>
                <h3 className="dw-empty-title">Ready for analysis</h3>
                <p className="dw-empty-sub">
                  Upload a chest X-ray on the left and click <strong>Run Analysis</strong> to receive
                  an AI-assisted TB screening result with a visual heatmap showing which regions of
                  the lung influenced the prediction.
                </p>
                <div className="dw-empty-hints">
                  <div className="dw-hint"><span className="dw-hint-num">1</span><span>Upload or drag a chest X-ray image</span></div>
                  <div className="dw-hint"><span className="dw-hint-num">2</span><span>Optionally add a patient reference ID</span></div>
                  <div className="dw-hint"><span className="dw-hint-num">3</span><span>Click Run Analysis — results appear here</span></div>
                </div>
              </div>
            )}

            {loading && (
              <div className="dw-loading">
                <div className="dw-loading-visual">
                  <div className="dw-loading-ring" />
                  <div className="dw-loading-inner-ring" />
                </div>
                <h3 className="dw-loading-title">Analyzing X-Ray</h3>
                <p className="dw-loading-sub">Running AI inference and generating lung heatmap…</p>
              </div>
            )}

            {hasResult && (
              <div className="dw-results">
                <div className={`dw-result-header ${isTB ? 'is-tb' : 'is-ok'}`}>
                  <div className="dw-result-left">
                    <div className={`dw-result-icon ${isTB ? 'icon-tb' : 'icon-ok'}`}>
                      {isTB
                        ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                      }
                    </div>
                    <div>
                      <p className="dw-result-eyebrow">AI Classification Result</p>
                      <h2 className="dw-result-verdict">{isTB ? 'TB Detected' : 'No TB Detected'}</h2>
                    </div>
                  </div>
                  <div className="dw-result-right">
                    <div className="dw-conf-ring-wrap">
                      <svg className="dw-conf-ring" viewBox="0 0 120 120">
                        <circle cx="60" cy="60" r="50" fill="none" stroke="currentColor" strokeWidth="6" strokeOpacity="0.12" />
                        <circle cx="60" cy="60" r="50" fill="none" stroke="currentColor" strokeWidth="6"
                          strokeDasharray={`${(confidence / 100) * 314} 314`}
                          strokeLinecap="butt" transform="rotate(-90 60 60)" />
                      </svg>
                      <div className="dw-conf-ring-text">
                        <span className="dw-conf-pct">{confidence}%</span>
                        <span className="dw-conf-lbl">confidence</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="dw-meta-row">
                  {patientRef && <div className="dw-meta-chip"><span className="dw-chip-label">Patient</span><span className="dw-chip-value">{patientRef}</span></div>}
                  {region    && <div className="dw-meta-chip"><span className="dw-chip-label">Primary region</span><span className="dw-chip-value">{region.replace(/\b\w/g, c => c.toUpperCase())}</span></div>}
                  {timestamp && <div className="dw-meta-chip"><span className="dw-chip-label">Timestamp</span><span className="dw-chip-value">{timestamp}</span></div>}
                </div>

                <div className="dw-main-grid">
                  {heatmapUrl && previewUrl && (
                    <div className="dw-gradcam-card">
                      <div className="dw-card-header">
                        <p className="dw-card-title">Lung Heatmap</p>
                      </div>
                      <div ref={sliderRef} className="dw-slider"
                        onMouseDown={(e) => { onMouseDown(e); setTourStep(s => s === 2 ? 3 : s); }} onMouseMove={onMouseMove}
                        onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
                        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
                        role="slider" aria-label="Compare original and heatmap"
                        aria-valuenow={Math.round(sliderPos)} aria-valuemin={0} aria-valuemax={100}>
                        <img src={heatmapUrl} alt="Lung heatmap" className="dw-sl-img"
                          style={{ clipPath: `inset(0 0 0 ${sliderPos}%)` }} draggable={false} />
                        <img src={previewUrl} alt="Original X-ray" className="dw-sl-img"
                          style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }} draggable={false} />
                        <span className="dw-sl-tag dw-sl-left">Original</span>
                        <span className="dw-sl-tag dw-sl-right">Heatmap</span>
                        <div className="dw-sl-handle" style={{ left: `${sliderPos}%` }}>
                          <div className="dw-sl-line" />
                          <div ref={sliderKnobRef} className="dw-sl-knob" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                              <path d="M5 12h14M8 8l-4 4 4 4M16 8l4 4-4 4" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <p className="dw-gradcam-note">
                        Highlighted regions indicate areas that most influenced the prediction.
                        Upper lobe activation is consistent with typical TB presentation.
                      </p>
                    </div>
                  )}

                  {explanation && (
                    <div className="dw-expl-card">
                      <div className="dw-card-header">
                        <p className="dw-card-title">AI Explanation</p>
                        <span className="dw-ai-badge">AI Generated</span>
                      </div>
                      <div className="dw-expl-body">
                        {(() => {
                          const SKIP = ['classification', 'confidence', 'primary region', 'timestamp'];
                          return explanation.split('\n')
                            .filter(l => l.trim())
                            .filter(l => !SKIP.some(k => l.toLowerCase().startsWith(k)))
                            .map((line, i) => {
                              const isHeading = /^[A-Z][A-Z\s]+:/.test(line);
                              const isKv = !isHeading && line.includes(':') && line.split(':')[0].trim().length < 25;
                              if (isHeading) return <p key={i} className="dw-expl-heading">{line}</p>;
                              if (isKv) {
                                const [k, ...rest] = line.split(':');
                                return <div key={i} className="dw-expl-kv"><span className="dw-expl-k">{k}:</span><span className="dw-expl-v">{rest.join(':')}</span></div>;
                              }
                              return <p key={i} className="dw-expl-p">{line}</p>;
                            });
                        })()}
                      </div>
                    </div>
                  )}
                </div>

                <div className="dw-actions-row">
                  <button ref={reportBtnRef} className={`dw-report-btn ${tourStep === 3 ? 'dw-run-btn-pulse' : ''}`}
                    onClick={() => { handleDownloadReport(); setTourStep(4); }} disabled={reportLoading}>
                    {reportLoading ? (
                      <><span className="dw-spinner dw-spinner-blue" /> Generating report…</>
                    ) : (
                      <>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download PDF Report
                      </>
                    )}
                  </button>
                  <button className="dw-new-btn" onClick={clearAll}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="1 4 1 10 7 10" />
                      <path d="M3.51 15a9 9 0 1 0 .49-3.5" />
                    </svg>
                    New Analysis
                  </button>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>

      {/* Portal tips — on body to escape panel overflow clipping */}
      {tourStep === 0 && !previewUrl && splashDone && <PortalTip anchorRef={sampleBtnRef} text="Start here — load a sample X-ray" arrow="←" offset={24} delay={2000} placement="right" />}
      {tourStep === 1 && splashDone && <PortalTip anchorRef={runBtnRef} text="Image loaded — click to run" arrow="←" offset={24} delay={2000} placement="right" />}
      {tourStep === 2 && splashDone && <PortalTip anchorRef={sliderKnobRef} text="Drag the slider to compare" arrow="↓" offset={14} delay={2000} placement="top" />}
      {tourStep === 3 && splashDone && <PortalTip anchorRef={reportBtnRef} text="Download a full clinical PDF report" arrow="↓" offset={14} delay={2000} placement="top-left" />}
    </>
  );
}
